import React from 'react'
import {
  Button,
  FormGroup,
  Label,
  Row,
  Col,
  Media,
  CustomInput,
  Alert,
  Input
} from 'reactstrap'
import { firstLetter, getCurrencySign } from 'common'
import { PackTemplatesApi } from 'typescript-axios'
import Cleave from 'cleave.js/react'
import { Slider } from '@mui/material'
import useTranslation from '../hooks/UseTranslation'
import GuruModal from './GuruModal'

import { getAxiosParams } from '../utils/client'
import PackCard from './PackCard'
import LoadingWidget from './LoadingWidget'
import DeleteModal from './DeleteModal'
import AsyncButton from './AsyncButton'
import Select from 'react-select';

const PACK_TEMPLATES_SERVICE = new PackTemplatesApi(getAxiosParams())
const PACK_NAME_MAX_LEN = 25

const WEEKS_1 = 1 / 4.34524
const WEEKS_2 = 2 / 4.34524
const WEEKS_3 = 3 / 4.34524

export const PackTemplatesList = (props) => {
  const [packTemplates, setPackTemplates] = React.useState(props.packTemplates)

  React.useEffect(() => {
    setPackTemplates(props.packTemplates)
  }, [props.packTemplates])

  const { onClick, onEdit, onDelete } = props
  return (
    packTemplates &&
    packTemplates.length > 0 && (
      <Row>
        {packTemplates.map((packTemplate, index) => (
          <Col key={index} xs='6' md='4' className='mb-5'>
            <PackCard
              key={`pack_${packTemplate.id}`}
              onClick={onClick}
              onEdit={onEdit}
              onDelete={onDelete}
              pack={packTemplate}
            />
          </Col>
        ))}
      </Row>
    )
  )
}

const nClassesWidget = (value, setter, disabled, t) => (
  <FormGroup>
    <Label className='form-label' id='onlineLabel'>
      {t('packs_add_n_classes')}
    </Label>
    <div>
      <Slider
        disabled={disabled}
        color='primary'
        valueLabelDisplay='auto'
        aria-label='slider'
        min={1}
        max={100}
        value={value}
        onChange={(e, newVal) => setter(newVal)}
      />
    </div>
    <div className='text-muted'>{t('packs_n_classes', value)}</div>
  </FormGroup>
)
const uniqueUseWidget = (value, setter, disabled, t) => {
  return <CustomInput
    className='text-muted mb-2'
    type='switch'
    disabled={disabled}
    id='is_unique_use'
    name='is_unique_use'
    key='is_unique_use'
    label={t('packs_is_unique_use')}
    checked={value}
    onChange={(e) => setter(e.target.checked)}
  />
}
const validityWidget = (value, setter, disabled, t) => {
  // Define options for the select dropdown
  const options = [
    { value: WEEKS_1, label: t('1_week') },
    { value: WEEKS_2, label: t('2_weeks') },
    { value: WEEKS_3, label: t('3_weeks') },
    { value: 1, label: t('1_month') },
    ...Array.from({ length: 11 }, (_, i) => ({
      value: i + 2,
      label: t('{}_months', i + 2),
    })),
    { value: 'no_expiration', label: t('pack_no_expiry') },
  ];

  // Find the selected option based on the value
  const selectedOption = options.find((option) => option.value === value);

  return (
    <FormGroup className='mb-4'>
      <Label className='form-label' id='onlineLabel'>
        {t('pack_validity')}
      </Label>
      <div>
        <Select
          isSearchable={false}
          isMulti={false}
          value={selectedOption}
          onChange={(option) => setter(option.value)}
          options={options}
          isDisabled={disabled}
          placeholder={firstLetter(t('default_select_placeholder'))}
          className='text-muted'
        />
      </div>
    </FormGroup>
  );
};

const typeOfClassWidget = (value, setter, disabled, t) => (
  <FormGroup className='mb-4'>
    <Label className='form-label' id='onlineLabel'>
      {t('type_of_class')}
    </Label>
    <ul className='list-inline mb-0'>
      <li key='pack_type_all' className='list-inline-item mr-4'>
        <CustomInput
          disabled={disabled}
          key='pack_type_all'
          type='radio'
          id='pack_type_all'
          name='pack_type_all'
          onChange={(e) => setter('all')}
          value={2}
          checked={value == 'all'}
          label={firstLetter(t('both_two'))}
          className='text-muted'
        />
      </li>
      <li key='pack_type_online' className='list-inline-item mr-4'>
        <CustomInput
          disabled={disabled}
          key='pack_type_online'
          type='radio'
          id='pack_type_online'
          name='pack_type_online'
          onChange={(e) => setter('online')}
          value={0}
          checked={value == 'online'}
          label={firstLetter(t('online'))}
          className='text-muted'
        />
      </li>
      <li key='pack_type_physic' className='list-inline-item'>
        <CustomInput
          disabled={disabled}
          key='pack_type_physic'
          type='radio'
          id='pack_type_physic'
          name='pack_type_physic'
          onChange={(e) => setter('physical')}
          value={1}
          checked={value == 'physical'}
          label={firstLetter(t('physical'))}
          className='text-muted'
        />
      </li>
    </ul>
  </FormGroup>
)

const priceWidget = (value, setter, disabled, status, nClasses, t) => (
  <FormGroup className='mb-4'>
    <Label className='form-label' id='onlineLabel'>
      {
        // fixme hardcoded currency
        t('price_in_currency', getCurrencySign('EUR'))
      }
    </Label>
    <Row>
      <Col xs='3'>
        <Cleave
          className='form-control'
          name='price'
          id='price'
          disabled={disabled}
          onChange={(e) => setter(e.target.rawValue)}
          value={value}
          options={{
            numeral: true,
            numeralPositiveOnly: true,
            numeralDecimalScale: 2
          }}
        />
      </Col>
      <Col xs='9'>
        {
          // fixme: currency hardcoded
          !status.disabled && (
            <div className='p-2 text-muted'>
              {t(
                'packs_summary',
                Math.round((value / nClasses) * 100) / 100,
                getCurrencySign('EUR')
              )}
            </div>
          )
        }
      </Col>
    </Row>
  </FormGroup>
)

const Packs = (props) => {
  const { t } = useTranslation()
  const [updateErrorMsg, setUpdateErrorMsg] = React.useState(null)
  const [errorMsg, setErrorMsg] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [packToDelete, setPackToDelete] = React.useState(null)
  const [packs, setPacks] = React.useState(props.packs)
  const [showAddModal, setShowAddModal] = React.useState(false)
  const [selectedPack, setSelectedPack] = React.useState(null)
  const [createStatus, setCreateStatus] = React.useState({
    disabled: true,
    exists: false
  })
  const [updateStatus, setUpdateStatus] = React.useState({
    disabled: true,
    exists: false
  })
  const [selectedPackType, setSelectedPackType] = React.useState('all')
  const [priceForCreate, setPriceForCreate] = React.useState()
  const [priceForUpdate, setPriceForUpdate] = React.useState()
  const [isUniqueForCreate, setIsUniqueForCreate] = React.useState(false)
  const [nameForUpdate, setNameForUpdate] = React.useState(null)
  const [nameForCreate, setNameForCreate] = React.useState(null)
  const [nClasses, setNClasses] = React.useState(10)
  const [validity, setValidity] = React.useState(12)
  const [validityForUpdate, setValidityForUpdate] = React.useState()
  const toggleAdd = () => setShowAddModal((showAddModal) => !showAddModal)

  React.useEffect(() => {
    setPacks(props.packs)
  }, [props.packs])

  React.useEffect(() => {
    if (selectedPack) {
      setNameForUpdate(
        selectedPack.name ??
        t('packs_n_classes_display', selectedPack.n_classes)
      )
    }
  }, [selectedPack])

  React.useEffect(() => {
    const everythingSelected = priceForCreate && selectedPackType && nClasses
    let existsAlready = false
    packs.map((pack) => {
      if (pack.n_classes == nClasses && pack.pack_type == selectedPackType && (
        !isUniqueForCreate && !pack.is_unique_use
      )) {
        existsAlready = true
      }
    })
    setCreateStatus({
      disabled: !everythingSelected || isLoading || existsAlready,
      exists: existsAlready
    })
  }, [priceForCreate, isLoading, selectedPackType, nClasses, isUniqueForCreate])

  React.useEffect(() => {
    if (!selectedPack) {
      return setCreateStatus({ disabled: true, exists: false })
    }
    const everythingSelected = Boolean(priceForUpdate)
    let existsAlready = false
    packs.map((pack) => {
      if (
        pack.id != selectedPack.id &&
        pack.n_classes == selectedPack.n_classes &&
        pack.pack_type == selectedPack.pack_type &&
        (!selectedPack.is_unique_use && !pack.is_unique_use)
      ) {
        existsAlready = true
      }
    })
    setUpdateStatus({
      disabled: !everythingSelected || isLoading || existsAlready,
      exists: existsAlready
    })
  }, [selectedPack, priceForUpdate, isLoading])

  React.useEffect(() => {
    if (selectedPack) {
      setPriceForUpdate(selectedPack.price)
      if (selectedPack.validity_duration) {
        const validityDurationMonth = selectedPack.validity_duration ? selectedPack.validity_duration / 3600 / 24 / 31 : null
        const closest = [WEEKS_1, WEEKS_2, WEEKS_3, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].reduce((prev, curr) =>
          Math.abs(curr - validityDurationMonth) < Math.abs(prev - validityDurationMonth)
            ? curr
            : prev
        )
        setValidityForUpdate(closest)
      } else {
        setValidityForUpdate(null)
      }
    }
  }, [selectedPack])

  const createPack = () => {
    setIsLoading(true)
    setErrorMsg(null)
    return PACK_TEMPLATES_SERVICE.createPackTemplateApiV1PackTemplatesPost({
      pack_type: selectedPackType,
      n_classes: nClasses,
      price: priceForCreate,
      name: nameForCreate,
      validity_duration: validity === null ? null : validity * 3600 * 24 * 31,
      is_unique_use: isUniqueForCreate
    })
      .then((res) => {
        setShowAddModal(false)
        props.onCreateCallback(res.data)
        setNameForCreate(null)
      })
      .catch((res) => {
        if (res.response.data.detail) {
          setErrorMsg(res.response.data.detail)
          console.error(res.response.data.detail)
        } else {
          console.error(res)
          setErrorMsg('modal_error_generic')
        }
      })
      .finally(() => setIsLoading(false))
  }

  const onDelete = (e, pack) => {
    e.preventDefault()
    setIsLoading(true)
    return PACK_TEMPLATES_SERVICE.updatePackTemplateApiV1PackTemplatesPackTemplateIdPut(
      pack.id,
      { is_active: false }
    )
      .then(() => {
        const index = packs.findIndex((p) => pack.id == p.id)
        if (index > -1) {
          packs.splice(index, 1)
        }
        props.onDeleteCallback && props.onDeleteCallback()
      })
      .finally(() => {
        setPackToDelete(null)
        setIsLoading(false)
      })
  }

  const updatePack = () => {
    setIsLoading(true)
    const validity_duration =
      validityForUpdate === null ? null : validityForUpdate * 3600 * 24 * 31
    return PACK_TEMPLATES_SERVICE.updatePackTemplateApiV1PackTemplatesPackTemplateIdPut(
      selectedPack.id,
      {
        name: nameForUpdate,
        price: priceForUpdate,
        validity_duration
      }
    )
      .then((res) => {
        const updatedPack = res.data
        setSelectedPack(null)
        const index = packs.findIndex((p) => p.id == updatedPack.id)
        packs[index] = updatedPack
        setPacks([...packs])
        setUpdateErrorMsg(null)
      })
      .catch((res) => {
        if (res.response.data.detail) {
          setUpdateErrorMsg(res.response.data.detail)
          console.error(res.response.data.detail)
        } else {
          console.error(res)
          setUpdateErrorMsg('modal_error_generic')
        }
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <div className='text-block'>
      <DeleteModal
        isOpen={packToDelete}
        toggle={() => setPackToDelete(null)}
        title={t('pack_delete_modal_title')}
        delete={(e) => onDelete(e, packToDelete)}
      />
      <GuruModal
        isOpen={Boolean(selectedPack)}
        toggle={() => setSelectedPack(null)}
        title={t('packs_edit_modal_title')}
        button={
          <>
            <Button
              disabled={isLoading}
              color='light'
              onClick={() => setSelectedPack(null)}
            >
              {t('cancel')}
            </Button>{' '}
            <AsyncButton
              disabled={isLoading}
              color='primary'
              onClick={() => updatePack()}
            >
              {t('update')}
            </AsyncButton>
          </>
        }
      >
        <Alert color='primary'>{t('update_pack_content')}</Alert>
        <FormGroup className='mb-4'>
          <Label className='form-label' id='onlineLabel'>
            {t('packs_name')}
          </Label>
          <Input
            maxlength={PACK_NAME_MAX_LEN}
            className='form-control'
            onChange={(e) => setNameForUpdate(e.target.value)}
            value={nameForUpdate}
          />
        </FormGroup>
        {typeOfClassWidget(selectedPack?.pack_type, () => null, true, t)}
        {nClassesWidget(selectedPack?.n_classes, () => null, true, t)}
        {validityWidget(validityForUpdate, setValidityForUpdate, false, t)}
        {priceWidget(
          priceForUpdate,
          setPriceForUpdate,
          false,
          updateStatus,
          selectedPack?.n_classes,
          t
        )}
        {uniqueUseWidget(selectedPack?.is_unique_use, () => null, true, t)}
        {updateErrorMsg && (
          <Alert color='danger'>
            <div className='p-2'>{updateErrorMsg}</div>
          </Alert>
        )}
      </GuruModal>
      <GuruModal
        isOpen={showAddModal}
        toggle={toggleAdd}
        onClosed={() => setNameForCreate(null)}
        button={
          <AsyncButton
            disabled={createStatus.disabled}
            color='primary'
            onClick={createPack}
          >
            {t('add')}
          </AsyncButton>
        }
        title={t('packs_add_modal_title')}
      >
        <div className='p-2'>
          <FormGroup className='mb-4'>
            <Label className='form-label' id='onlineLabel'>
              {t('packs_name')}
            </Label>
            <Input
              className='form-control'
              maxlength={PACK_NAME_MAX_LEN}
              onChange={(e) => setNameForCreate(e.target.value)}
              value={nameForCreate ?? t('packs_n_classes_display', nClasses)}
            />
          </FormGroup>
          {typeOfClassWidget(selectedPackType, setSelectedPackType, false, t)}
          {nClassesWidget(nClasses, setNClasses, false, t)}
          {validityWidget(validity, setValidity, false, t)}
          {priceWidget(
            priceForCreate,
            setPriceForCreate,
            false,
            createStatus,
            nClasses,
            t
          )}
          {uniqueUseWidget(isUniqueForCreate, setIsUniqueForCreate, false, t)}
          {errorMsg && (
            <Alert color='danger'>
              <div className='p-2'>{errorMsg}</div>
            </Alert>
          )}
          {!errorMsg && createStatus.exists && (
            <Alert color='warning'>
              <div className='p-2'>{t('packs_exists_already')}</div>
            </Alert>
          )}
        </div>
      </GuruModal>
      <h5 className='text-first-letter mb-4'>{t('packs')}</h5>
      <Media className='text-muted mb-4'>
        <Media body className='mt-n1'>
          <div
            dangerouslySetInnerHTML={{
              __html: t('packs_and_subscriptions_page_content')
            }}
          />
        </Media>
      </Media>
      <div>
        {packs === null && <LoadingWidget />}
        {packs && packs.length == 0 && (
          <p className='text-muted mb-4'>{t('packs_no_packs')}</p>
        )}
        <PackTemplatesList
          packTemplates={packs}
          onEdit={setSelectedPack}
          onDelete={(e, pack) => setPackToDelete(pack)}
        />
        {packs !== null && (
          <Button color='outline-primary' onClick={toggleAdd}>
            <i className='fa fa-plus fa-fw mr-2 text-right' />
            {t('packs_create_cta')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default Packs
